import { type CSSProperties } from 'react'
import {
  type LucideIcon,
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  Award,
  Badge,
  BadgeCheck,
  Ban,
  Bell,
  BellDot,
  BellRing,
  BookOpen,
  Bot,
  Briefcase,
  Camera,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleStop,
  ClipboardList,
  Clock,
  CloudUpload,
  Copy,
  Download,
  FilePen,
  FileText,
  FolderOpen,
  Gavel,
  GraduationCap,
  Heart,
  HelpCircle,
  History,
  Home,
  Hourglass,
  Image as ImageIcon,
  Info,
  Keyboard,
  LifeBuoy,
  LayoutDashboard,
  Lock,
  LockOpen,
  LogOut,
  Menu,
  Mic,
  MessageCircle,
  MessageSquareText,
  MoreVertical,
  PanelLeftClose,
  Paperclip,
  PenLine,
  Pencil,
  Phone,
  PieChart,
  Pin,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  RotateCw,
  Scale,
  Search,
  SearchX,
  Send,
  Settings,
  Share2,
  Shield,
  Smartphone,
  Sparkles,
  Star,
  ThumbsUp,
  Trash2,
  Type,
  User,
  UserMinus,
  UserX,
  Users,
  Video,
  Wallet,
  X,
  XCircle,
  Zap,
} from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Material Symbols 名 → Lucide 图标组件 映射
 *
 * 设计原则: 保留 `<Icon name="..." />` 调用方 API 不变, 内部从字体 ligature 换为
 * Lucide React SVG 组件; 按需 tree-shake, 零字体加载等待.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  account_balance_wallet: Wallet,
  add: Plus,
  arrow_back: ArrowLeft,
  arrow_downward: ArrowDown,
  arrow_forward: ArrowRight,
  arrow_upward: ArrowUp,
  assignment: ClipboardList,
  attach_file: Paperclip,
  auto_awesome_mosaic: Sparkles,
  autorenew: RotateCw,
  badge: Badge,
  balance: Scale,
  block: Ban,
  bolt: Zap,
  cached: History,
  cancel: XCircle,
  chat: MessageCircle,
  check: Check,
  check_circle: CheckCircle,
  chevron_left: ChevronLeft,
  chevron_right: ChevronRight,
  cloud_upload: CloudUpload,
  close: X,
  content_copy: Copy,
  description: FileText,
  download: Download,
  edit: Pencil,
  edit_document: FilePen,
  edit_note: PenLine,
  error: AlertCircle,
  'warning-circle': AlertCircle,
  family_restroom: Users,
  favorite: Heart,
  feedback: MessageSquareText,
  folder_open: FolderOpen,
  gavel: Gavel,
  help: HelpCircle,
  help_outline: HelpCircle,
  house: Home,
  image: ImageIcon,
  info: Info,
  keyboard_voice: Mic,
  keyboard: Keyboard,
  lock: Lock,
  lock_open: LockOpen,
  logout: LogOut,
  menu: Menu,
  menu_book: BookOpen,
  menu_open: PanelLeftClose,
  more_vert: MoreVertical,
  notification_important: BellDot,
  notifications: Bell,
  notifications_active: BellRing,
  payments: Wallet,
  pending: Hourglass,
  person: User,
  person_off: UserX,
  person_remove: UserMinus,
  phone_in_talk: Phone,
  phone_iphone: Smartphone,
  photo_camera: Camera,
  pie_chart: PieChart,
  push_pin: Pin,
  print: Printer,
  receipt_long: Receipt,
  recommend: ThumbsUp,
  refresh: RefreshCw,
  schedule: Clock,
  school: GraduationCap,
  search: Search,
  search_off: SearchX,
  send: Send,
  settings: Settings,
  share: Share2,
  shield: Shield,
  smart_toy: Bot,
  smartphone: Smartphone,
  star: Star,
  stop_circle: CircleStop,
  support_agent: LifeBuoy,
  sync: RefreshCw,
  task_alt: CheckCircle2,
  delete: Trash2,
  text_fields: Type,
  verified: BadgeCheck,
  verified_user: BadgeCheck,
  videocam: Video,
  work: Briefcase,
  dashboard: LayoutDashboard,
  workspace_premium: Award,
}

interface IconProps {
  /** 图标名 (沿用 Material Symbols 命名, 内部映射到 Lucide) */
  name: string
  /** 是否填充 (针对 Star/Heart/BadgeCheck 等有闭合内部的图标有意义) */
  filled?: boolean
  /** Tailwind 类, size 默认 1em 跟随 font-size */
  className?: string
  style?: CSSProperties
  'aria-hidden'?: boolean
}

/**
 * 图标包装组件
 *
 * 内部基于 Lucide React, SVG 随 JS bundle 一起到达, 不依赖外部字体.
 * 大小通过 font-size 控制 (1em), 保持与原 Material Symbols 体验一致.
 */
export function Icon({ name, filled = false, className, style, ...rest }: IconProps) {
  const Comp = ICON_MAP[name]
  if (!Comp) {
    if (import.meta.env.DEV) {
      console.warn(`[Icon] 未映射的图标名: "${name}"`)
    }
    return null
  }
  return (
    <Comp
      className={cn('inline-block shrink-0 align-[-0.125em]', className)}
      style={{ width: '1em', height: '1em', ...style }}
      fill={filled ? 'currentColor' : 'none'}
      aria-hidden={rest['aria-hidden'] ?? true}
    />
  )
}
